/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Polygon, FeatureCollection, GeoJsonProperties } from 'geojson';
import type { Feature as OlFeature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { Polygon as OlPolygon } from 'ol/geom';

const DISTANCE_TWO_POINTS_ARE_CONNECTED = 0.00001;

const distanceTwoCoords = (coordA: number[], coordB: number[]): number => {
  return Math.hypot(coordB[0] - coordA[0], coordB[1] - coordA[1]);
};

const angleTwoCoords = (coordA: number[], coordB: number[]): number => {
  return Math.atan2(coordB[0] - coordA[0], coordB[1] - coordA[1]);
};

/**
 * Removes lines that are reversed in direction
 * @param newPolyCoords List of coordinates
 * @returns Cleaned up list of coordinates.
 */
export const removeOverlappingLines = (
  newPolyCoords: Coordinate[],
  props: { angleTolerance: number } = { angleTolerance: 0.01 },
): Coordinate[] => {
  const { angleTolerance } = props;
  const indicesToRemove: number[] = [];
  newPolyCoords.forEach((coordsA, index): void => {
    const coordsB = newPolyCoords[(index + 1) % newPolyCoords.length];
    const coordsC = newPolyCoords[(index + 2) % newPolyCoords.length];
    const angleA = angleTwoCoords(coordsA, coordsB);
    const angleB = angleTwoCoords(coordsB, coordsC);
    if (Math.abs(Math.abs(angleA - angleB) - Math.PI) < angleTolerance) {
      indicesToRemove.push((index + 1) % newPolyCoords.length);
    }
  });
  // Remove indices, starting with the highest index, therefore sort the indices from high to low
  indicesToRemove.sort((itemA: number, itemB: number): number => itemB - itemA);
  indicesToRemove.forEach((index) => {
    newPolyCoords.splice(index, 1);
  });

  // Check if polys still close, if the distance is too much, add the begin point to the end.
  if (
    distanceTwoCoords(
      newPolyCoords[0],
      newPolyCoords[newPolyCoords.length - 1],
    ) > DISTANCE_TWO_POINTS_ARE_CONNECTED
  ) {
    newPolyCoords.push(newPolyCoords[0]);
  }
  return newPolyCoords;
};

export const polygonCleanDanglingCoords = (
  polyCoords: Coordinate[],
  props: { distanceTolerance: number; angleTolerance: number } = {
    distanceTolerance: 0,
    angleTolerance: 0.01,
  },
): Coordinate[] => {
  const newPolyCoords: Coordinate[] = [];
  // Calculate the distance between each point.
  const distanceSet = polyCoords.map((polyCoordOrg): number[] => {
    return polyCoords.map((polyCoordNest): number => {
      return polyCoordOrg === polyCoordNest
        ? NaN
        : distanceTwoCoords(polyCoordOrg, polyCoordNest);
    });
  });

  // Sort the distance to the smallest and take the first
  const flatDistance = distanceSet.map((aItem) => {
    return aItem.sort(
      (itemA: number, itemB: number): number => itemA - itemB,
    )[0];
  });
  // State to keep track which coordinates overlapped with another
  let stateTriggerFoundKnot: Coordinate | undefined;
  polyCoords.forEach((value, index) => {
    // Start and end of the polygon should be ommitted, because they are always the same.
    if (index > 0) {
      if (!stateTriggerFoundKnot) {
        if (flatDistance[index] <= props.distanceTolerance) {
          stateTriggerFoundKnot = value;
        }
      } else if (
        distanceTwoCoords(stateTriggerFoundKnot, value) <=
        props.distanceTolerance
      ) {
        stateTriggerFoundKnot = undefined;
      }
    } else {
      stateTriggerFoundKnot = undefined;
    }
    if (!stateTriggerFoundKnot) {
      newPolyCoords.push(value);
    }
  });
  return removeOverlappingLines(newPolyCoords, props);
};

/**
 * This cleans dangling line segments from a polygon. The distanceTolerance can be set to remove small segments too.
 * @param features Feature to clean, currently only Polygons are supported.
 * @param distanceTolerance The distanceTolerance in the same coordinate system as the feature
 * @returns cleaned up features
 */
export const polygonCleanDangling = (
  features: OlFeature[],
  props: { distanceTolerance: number; angleTolerance: number } = {
    distanceTolerance: 0,
    angleTolerance: 0.01,
  },
): OlFeature[] => {
  return features.map((feature) => {
    if (feature.getGeometry()?.getType() === 'Polygon') {
      const featureGeometry = (feature as OlFeature<OlPolygon>).getGeometry();
      const featureCoordSet = featureGeometry?.getCoordinates();
      if (featureCoordSet?.length) {
        const newFeatureCoords: Coordinate[][] = featureCoordSet.map(
          (coordinates: Coordinate[]): Coordinate[] => {
            return polygonCleanDanglingCoords(coordinates, props);
          },
        );
        featureGeometry?.setCoordinates(newFeatureCoords);
      }
    }
    return feature;
  });
};

/**
 * This cleans dangling line segments from a polygon. The distanceTolerance can be set to remove small segments too.
 * @param features Feature to clean, currently only Polygons are supported.
 * @param distanceTolerance The distanceTolerance in the same coordinate system as the feature
 * @returns cleaned up features
 */
export const polygonFeatureCollectionCleanDangling = (
  features: FeatureCollection<Polygon, GeoJsonProperties> | null,
  props: { distanceTolerance: number; angleTolerance: number } = {
    distanceTolerance: 0,
    angleTolerance: 0.01,
  },
): FeatureCollection<Polygon, GeoJsonProperties> | null => {
  features?.features.forEach((feature) => {
    // eslint-disable-next-line no-param-reassign
    feature.geometry.coordinates = feature.geometry.coordinates.map(
      (polyCoord) => polygonCleanDanglingCoords(polyCoord, props),
    );
  });

  return features;
};
